/**
 * Colour themes for roster org-chart nodes.
 *
 * ICS section identity is preserved — Operations still reads red, Planning
 * blue, and so on — but each section is expressed as a border paired with a
 * translucent surface wash instead of a saturated outline on white. The values
 * themselves live in `styles/_root.scss`.
 */
export type RosterNodeTheme = {
  border: string;
  surface: string;
};

const theme = (name: string): RosterNodeTheme => ({
  border: `var(--ics-${name}-border)`,
  surface: `var(--ics-${name}-surface)`,
});

/**
 * Keyed by the section's chief seat, which is how a position's section is
 * resolved: walk parents until one matches.
 */
export const SECTION_THEME_BY_SEAT = new Map<string, RosterNodeTheme>([
  ['Operations Section Chief', theme('operations')],
  ['Planning Section Chief', theme('planning')],
  ['Logistics Section Chief', theme('logistics')],
  ['Finance Section Chief', theme('finance')],
  ['Intel/Investigations Section Chief', theme('intel')],
  ['Incident Commander', theme('command')],
  // TEMA State EOC section seats
  ['TEMA Director', theme('command')],
  ['Direction and Coordination Officer (DACO)', theme('command')],
  ['MCC Section', theme('mcc')],
  ['Operations Section', theme('operations')],
  ['Planning Section', theme('planning')],
  ['Logistics Section', theme('logistics')],
  ['Finance and Admin Section', theme('finance')],
]);

/** Used by command staff and by any seat whose parent chain reaches no section. */
export const NEUTRAL_NODE_THEME = theme('command');

/**
 * Resources sit outside the ICS sections, so they stay neutral rather than
 * borrowing their parent's colour.
 */
export const RESOURCE_NODE_THEME = theme('command');

export const TECHNICAL_SPECIALIST_THEME = theme('technical-specialist');

export const BRANCH_NODE_THEME = theme('branch');
export const ESF_NODE_THEME = theme('esf');

/** TEMA / ICS branch boxes — yellow on the paper chart. */
export function isBranchSeatName(name: string): boolean {
  return name.endsWith(' Branch') || name.includes(' Branch');
}

/** TEMA ESF boxes — orange/peach on the paper chart. */
export function isEsfSeatName(name: string): boolean {
  return name.startsWith('ESF-');
}
